"use client";

import { CredentialResponse } from "@react-oauth/google";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { buildSignupData } from "@/components/analytics/utm-trackers";
import { formMode } from "@/components/auth/form-mode";
import GoogleSignInButtonView from "@/components/auth/google-sign-in-button.view";
import useGoogleSsoAuth from "@/hooks/auth/use-google-sso-auth";
import useLastViewedState from "@/hooks/use-last-viewed-state";
import { NimbleProduct } from "@/lib/constants";
import {
  AUTH_AUDIENCES,
  type AuthAudience,
} from "@/lib/constants/auth-audiences";
import { GoogleOauthProvider } from "@/providers/google-oauth-provider";

interface GoogleSignInButtonContainerProps {
  onError?: (error: string) => void;
  onSignInSuccess: () => Promise<void>;
  audience: AuthAudience;
  mode: formMode;
}

const googleGeneralErrorMessage = "Failed to sign in with Google";
const googleNoCredentialErrorMessage = "No credential received from Google";

const DEFAULT_BUTTON_WIDTH = 384;

export default function GoogleSignInButtonContainer({
  onError,
  onSignInSuccess,
  audience,
  mode,
}: GoogleSignInButtonContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number>(DEFAULT_BUTTON_WIDTH);
  const googleSSOAuth = useGoogleSsoAuth();
  const pathname = usePathname();
  const lastViewedState = useLastViewedState();

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setButtonWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const handleGoogleSuccess = async (
    credentialResponse: CredentialResponse,
  ) => {
    if (googleSSOAuth.isPending) return;
    if (!credentialResponse.credential) {
      onError?.(googleNoCredentialErrorMessage);
      return;
    }

    try {
      if (audience === AUTH_AUDIENCES.DISTRICT_STAFF) {
        await googleSSOAuth.mutateAsync({
          google_token: credentialResponse.credential,
          nimble_product: NimbleProduct.ATS,
          mode: "login_only",
        });
      } else {
        const { signup_action, signup_state, utmTrackers } = buildSignupData(
          lastViewedState.stateConfig?.route,
        );

        const signupData = {
          ...utmTrackers,
          product: "connect",
          state: signup_state,
          signup_action,
        };

        document.cookie = `signup_data=${JSON.stringify(signupData)}; path=/; max-age=300`;

        await googleSSOAuth.mutateAsync({
          google_token: credentialResponse.credential,
          nimble_product: NimbleProduct.CONNECT,
          state: signup_state,
          redirect_to: pathname,
          signup_action,
          mode: mode === formMode.signin ? "login_only" : "login_or_signup",
        });
      }

      await onSignInSuccess();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : googleGeneralErrorMessage;
      onError?.(errorMessage);
    }
  };

  const handleGoogleError = () => {
    onError?.(googleGeneralErrorMessage);
  };

  return (
    <GoogleOauthProvider>
      <GoogleSignInButtonView
        containerRef={containerRef}
        buttonWidth={buttonWidth}
        isAuthenticating={googleSSOAuth.isPending}
        text={mode === formMode.signup ? "signup_with" : "signin_with"}
        onSuccess={handleGoogleSuccess}
        onError={handleGoogleError}
      />
    </GoogleOauthProvider>
  );
}
